'use client';

import React, { createContext, useContext, useLayoutEffect, useEffect } from 'react';
import { EcommerceConfig } from '@/lib/types';
import { generateColorShades } from '@/lib/utils/colors';

interface ThemeContextValue {
  config: EcommerceConfig;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  config: EcommerceConfig;
  children: React.ReactNode;
}

// Use useLayoutEffect on client, useEffect on server (SSR safe)
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function ThemeProvider({ config, children }: ThemeProviderProps) {
  // Generate CSS variables string for inline style injection (namespaced with --hp-)
  const cssVariables = React.useMemo(() => {
    const primaryShades = generateColorShades(config.colors.primary);
    const secondaryShades = generateColorShades(config.colors.secondary);
    const accentShades = generateColorShades(config.colors.accent);

    const vars: string[] = [];

    // Base colors (namespaced)
    vars.push(`--hp-primary: ${config.colors.primary}`);
    vars.push(`--hp-primary-dark: ${config.colors.primaryDark}`);
    vars.push(`--hp-secondary: ${config.colors.secondary}`);
    vars.push(`--hp-accent: ${config.colors.accent}`);
    vars.push(`--hp-accent-dark: ${config.colors.accentDark}`);
    vars.push(`--hp-text-hmuted: ${config.colors.textMuted}`);

    // Shaded colors (namespaced)
    Object.entries(primaryShades).forEach(([shade, rgb]) => {
      vars.push(`--hp-primary-${shade}: ${rgb}`);
    });
    Object.entries(secondaryShades).forEach(([shade, rgb]) => {
      vars.push(`--hp-secondary-${shade}: ${rgb}`);
    });
    Object.entries(accentShades).forEach(([shade, rgb]) => {
      vars.push(`--hp-accent-${shade}: ${rgb}`);
    });

    return vars.join('; ');
  }, [config.colors]);

  // Apply CSS variables to document root on client (fires before paint)
  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;
    const primaryShades = generateColorShades(config.colors.primary);
    const secondaryShades = generateColorShades(config.colors.secondary);
    const accentShades = generateColorShades(config.colors.accent);

    // Set base color variables (namespaced)
    root.style.setProperty('--hp-primary', config.colors.primary);
    root.style.setProperty('--hp-primary-dark', config.colors.primaryDark);
    root.style.setProperty('--hp-secondary', config.colors.secondary);
    root.style.setProperty('--hp-accent', config.colors.accent);
    root.style.setProperty('--hp-accent-dark', config.colors.accentDark);
    root.style.setProperty('--hp-text-hmuted', config.colors.textMuted);

    // Set shaded color variables (namespaced)
    Object.entries(primaryShades).forEach(([shade, rgb]) => {
      root.style.setProperty(`--hp-primary-${shade}`, rgb);
    });
    Object.entries(secondaryShades).forEach(([shade, rgb]) => {
      root.style.setProperty(`--hp-secondary-${shade}`, rgb);
    });
    Object.entries(accentShades).forEach(([shade, rgb]) => {
      root.style.setProperty(`--hp-accent-${shade}`, rgb);
    });
  }, [config]);

  return (
    <ThemeContext.Provider value={{ config }}>
      {/* Inject CSS variables via style tag for SSR - ensures colors are available immediately */}
      <style dangerouslySetInnerHTML={{ __html: `:root { ${cssVariables} }` }} />
      {children}
    </ThemeContext.Provider>
  );
}
